import { isEqual } from 'lodash'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { IRootStore } from 'store/types'
import { signboardFormatOptions, signboardSizeOptions } from 'sections/LKSection/shared/PickPointSharedModule/data'
import { PickPointFilesViewModel } from 'sections/LKSection/shared/PickPointSharedModule/models/PickPointFilesViewModel'
import { BaseOption } from 'shared/types/types'

import { SignboardFormat, SignboardSize } from '../../types'
import { OrderCCTVModel } from '../models/OrderCCTVModel/OrderCCTVModel'
import { ActivateOrderApiStore } from './ActivateOrderApiStore'
import { OrderActivateInitialStateType } from './types'

export class ActivateOrderUiStore {
  @observable selectedStep = 1

  @observable signboardSizeItem: BaseOption<SignboardSize> | null = null
  @observable signboardFormatItem: BaseOption<SignboardFormat> | null = null
  @observable comment = ''
  @observable initialized = false

  @observable fileList = new PickPointFilesViewModel()
  @observable cctv = new OrderCCTVModel()

  @observable ownerGln = ''
  @observable glnPvz = ''
  @observable molName = ''

  readonly apiStore = new ActivateOrderApiStore(this)
  private initialState: OrderActivateInitialStateType | null = null

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)
  }

  get isNoChanges() {
    return isEqual(this.initialState, this.hashFormState)
  }

  @computed
  get selectedOrder() {
    return this.rootStore.viewOrderUiStore.selectedOrder
  }

  @computed
  get orderId() {
    return this.rootStore.viewOrderUiStore.selectedOrder?.id
  }

  @computed
  get pvzId() {
    return this.rootStore.viewOrderUiStore.selectedOrder?.pickpointId
  }

  get hashFormState(): OrderActivateInitialStateType {
    const fileList = this.fileList

    return {
      signboardSize: this.signboardSizeItem?.value,
      signboardFormat: this.signboardFormatItem?.value,
      videos: fileList.videos.map((video) => video.url),
      documents: fileList.documents.map((document) => document.url),
      photos: fileList.photos.map((photo) => ({ url: photo.url, isPublic: photo.isPublic })),
      vendorValue: this.cctv.vendorValue,
      url: this.cctv.url.trim(),
      login: this.cctv.login.trim(),
      password: this.cctv.password.trim(),
      token: this.cctv.token.trim(),
      description: this.cctv.description.trim(),
      comment: this.comment.trim(),
      ownerGln: this.ownerGln.trim(),
      glnPvz: this.glnPvz.trim(),
      molName: this.molName.trim(),
    }
  }

  init = async () => {
    if (!this.selectedOrder?.id) {
      return
    }

    const order = this.selectedOrder

    this.setSignboardSizeItem(signboardSizeOptions.find((size) => size.value === order.signboardSize) || null)
    this.setSignboardFormatItem(signboardFormatOptions.find((format) => format.value === order.signboardFormat) || null)

    this.setGlnPvz(order.glnPvz)
    this.setMolName(order.molName)
    this.setOwnerGln(order.ownerGln)

    if (order.videos?.length && order.videos[0]?.url) {
      this.fileList.setVideoURL(order.videos[0].url)
    }
    this.fileList.setPhotos([...order.photos, ...order.documents])

    this.setInitialState(this.hashFormState)

    this.setInitialized(true)
  }

  @action
  setSelectedStep = (value: number) => {
    this.selectedStep = value
  }

  @action
  setSignboardSizeItem = (value: BaseOption<SignboardSize> | null) => {
    this.signboardSizeItem = value
  }

  @action
  setSignboardFormatItem = (value: BaseOption<SignboardFormat> | null) => {
    this.signboardFormatItem = value
  }

  @action
  setInitialState = (value: OrderActivateInitialStateType | null) => {
    this.initialState = value
  }

  @action
  setComment = (value: string) => {
    this.comment = value
  }

  @action
  setInitialized(value: boolean) {
    this.initialized = value
  }

  @action
  setOwnerGln = (value: string) => {
    this.ownerGln = value
  }

  @action
  setGlnPvz = (value: string) => {
    this.glnPvz = value
  }

  @action
  setMolName = (value: string) => {
    this.molName = value
  }

  @action
  resetBYCompanyInfo = () => {
    this.setOwnerGln('')
    this.setGlnPvz('')
    this.setMolName('')
  }

  @action
  reset = () => {
    this.setSignboardSizeItem(null)
    this.setSignboardFormatItem(null)
    this.setComment('')
    this.setInitialized(false)
    this.resetBYCompanyInfo()

    this.fileList.reset()
    this.cctv.resetCCTVInfo()
  }

  async updateOrder(orderId: number, disableLoadList: boolean = false) {
    return await this.apiStore.updateOrder(orderId, disableLoadList)
  }

  async makeOrderReadyActivate(orderId: number) {
    return await this.apiStore.makeOrderReadyActivate(orderId)
  }
}
